import { Ref, useEffect, useImperativeHandle, useMemo, useState } from 'react';

import { Address } from '@ravago/shared/page-data/models/elements/address.model';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Button } from '@ravago/shared/radiance/components/button/components/Button/Button';
import { Icon } from '@ravago/shared/radiance/components/icon/components/Icon/Icon';
import { DropdownOption } from '@ravago/shared/radiance/components/input-field/components/Dropdown/Dropdown';
import { RadioGroup } from '@ravago/shared/radiance/components/radio/components/RadioGroup/RadioGroup';
import { Tooltip } from '@ravago/shared/radiance/components/tooltip/components/Tooltip/Tooltip';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useTranslations } from '@ravago/shared/translations/client';

import { Controller, useForm, useWatch } from 'react-hook-form';

import { RequestForQuotationStep } from '../../../../models/request-for-quotation-step';
import { RfqAddressPicker } from '../fields/rfq-address-picker';
import { RfqTimeframeOptions } from '../fields/rfq-timeframe-options';

interface LocalFormValue {
   timeframe: DropdownOption | undefined;
   deliveryAddress: Address | undefined;
}

export interface FormValue {
   timeframe: DropdownOption;
   deliveryAddress: Address;
}

interface Props {
   stepRef: Ref<RequestForQuotationStep>;
   addresses: Address[];
   defaultFormValue: FormValue | undefined;
   onSubmit: (value: FormValue) => void;
}

export const RfqFlyoutStepAddressDetails = ({ stepRef, addresses, defaultFormValue, onSubmit }: Props) => {
   const translate = useTranslations();

   const [expanded, setExpanded] = useState(false);

   const toggleDeliveryAddressExpand = () => {
      setExpanded((curr) => !curr);
   };

   const deliveryAddresses = addresses.filter((address) => address.type === 'Delivery Address');

   const { control, handleSubmit } = useForm<LocalFormValue, unknown, FormValue>({
      defaultValues: defaultFormValue ?? {
         timeframe: undefined,
         deliveryAddress: deliveryAddresses.find((address) => address.isDefault)
      }
   });

   const deliveryAddress = useWatch({
      control,
      name: 'deliveryAddress'
   });

   const filteredAddresses = useMemo(() => {
      if (expanded) return deliveryAddresses.sort((address) => (address.id === deliveryAddress?.id ? -1 : 0));
      const selectedAddress = deliveryAddresses.find((address) => {
         if (deliveryAddress) {
            return address.id === deliveryAddress.id;
         }
         return address.isDefault;
      });

      if (!selectedAddress) return [];

      return [selectedAddress];
   }, [expanded, deliveryAddresses, deliveryAddress]);

   useEffect(() => {
      const hasDefaultAddress = deliveryAddresses.some((address) => address.isDefault);

      if (!hasDefaultAddress && !deliveryAddress) {
         setExpanded(true);
      }
   }, [deliveryAddresses]);

   useEffect(() => {
      if (deliveryAddress) {
         setExpanded(false);
      }
   }, [deliveryAddress]);

   useImperativeHandle(stepRef, () => ({
      submit: async () => {
         await handleSubmit((value) => onSubmit(value))();
      }
   }));

   return (
      <Box direction="column">
         <RfqTimeframeOptions control={control} name="timeframe" />

         <Box gap="md" direction="column">
            <Box direction="row" gap="2xs" items="center">
               <Typography component="span" variant="body-highlight">
                  {translate('page-consumer.request-for-quotation.delivery')}
               </Typography>
               <Tooltip align="start" pointers text={translate('page-consumer.request-for-quotation.tooltip.delivery')}>
                  <Box>
                     <Icon icon="info-circle" />
                  </Box>
               </Tooltip>
            </Box>
            <Box gap="md" direction="column">
               <Controller
                  control={control}
                  name="deliveryAddress"
                  rules={{
                     required: translate('form-validation.required', { field: translate('form-field.address') })
                  }}
                  render={({ field: { ref, ...field }, fieldState: { error } }) => (
                     <RadioGroup {...field} errorMessage={error?.message}>
                        {filteredAddresses.map((address) => (
                           <RfqAddressPicker key={address.id} address={address} selectedAddressId={deliveryAddress?.id.toString()} />
                        ))}
                     </RadioGroup>
                  )}
               />
               <Button
                  variant="tertiary"
                  fullWidth
                  onClick={() => toggleDeliveryAddressExpand()}
                  trailingIcon={expanded ? 'chevron-up' : 'chevron-down'}
               >
                  {expanded ? translate('page-consumer.request-for-quotation.show-less') : translate('page-consumer.request-for-quotation.show-more')}
               </Button>
            </Box>
         </Box>
      </Box>
   );
};
